"use client";

import * as React from "react";
import { CalendarIcon, ChevronDown, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { HTTP_METHODS } from "@/types/filters";
import {
  DEFAULT_ROUTE_ANALYTICS_PRESET,
  type RouteAnalyticsTimePreset,
  ROUTE_ANALYTICS_TIME_PRESETS,
} from "@/types/route-analytics";
import {
  combineUtcCalendarDateAndTime,
  formatUtcFilterInstant,
  formatUtcTimeOfDay,
  utcInstantToLocalCalendarDate,
} from "@/utils/date-utils";
import { cn } from "@/lib/utils";

const DEFAULT_FROM_TIME = "00:00:00";
const DEFAULT_TO_TIME = "23:59:59";

const STATUS_CODE_OPTIONS = [200, 201, 204, 400, 401, 403, 404, 409, 422, 429, 500, 502, 503];

function MultiSelectFilter({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 justify-between gap-1.5 text-xs" aria-label={label}>
          {label}
          {selected.length > 0 ? (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {selected.length}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => onToggle(option)}
                    className="flex items-center gap-2"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-3.5 shrink-0 rounded-sm border",
                        isSelected ? "border-primary bg-primary" : "border-input",
                      )}
                    />
                    <span className="truncate">{option}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CustomDateRangeButton({
  from,
  to,
  onChange,
}: {
  from: Date | undefined;
  to: Date | undefined;
  onChange: (from: Date | undefined, to: Date | undefined) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [draftRange, setDraftRange] = React.useState<{ from?: Date; to?: Date }>({});
  const [fromTime, setFromTime] = React.useState(DEFAULT_FROM_TIME);
  const [toTime, setToTime] = React.useState(DEFAULT_TO_TIME);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftRange({
        from: from ? utcInstantToLocalCalendarDate(from) : undefined,
        to: to ? utcInstantToLocalCalendarDate(to) : undefined,
      });
      setFromTime(from ? formatUtcTimeOfDay(from) : DEFAULT_FROM_TIME);
      setToTime(to ? formatUtcTimeOfDay(to) : DEFAULT_TO_TIME);
    }
    setOpen(next);
  };

  const draftHasDates = Boolean(draftRange.from || draftRange.to);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" aria-label="Custom date range">
          <CalendarIcon className="size-3.5" aria-hidden="true" />
          Custom
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          <Calendar
            mode="range"
            selected={{ from: draftRange.from, to: draftRange.to }}
            onSelect={(range) => setDraftRange({ from: range?.from, to: range?.to })}
            numberOfMonths={1}
          />
          <div className="flex w-full flex-col justify-center gap-3 border-t p-3 sm:w-40 sm:border-t-0 sm:border-l">
            <div className="space-y-1.5">
              <Label htmlFor="route-analytics-from-time" className="text-xs text-muted-foreground">
                From time
              </Label>
              <Input
                id="route-analytics-from-time"
                type="time"
                step={1}
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value || DEFAULT_FROM_TIME)}
                className="h-8 text-xs"
                disabled={!draftHasDates}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="route-analytics-to-time" className="text-xs text-muted-foreground">
                To time
              </Label>
              <Input
                id="route-analytics-to-time"
                type="time"
                step={1}
                value={toTime}
                onChange={(e) => setToTime(e.target.value || DEFAULT_TO_TIME)}
                className="h-8 text-xs"
                disabled={!draftHasDates}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 border-t p-2">
          <Button
            type="button"
            size="sm"
            className="flex-1 text-xs"
            disabled={!draftHasDates}
            onClick={() => {
              if (!draftRange.from && !draftRange.to) return;
              const startDate = draftRange.from ?? draftRange.to!;
              const endDate = draftRange.to ?? draftRange.from!;
              onChange(
                combineUtcCalendarDateAndTime(startDate, fromTime),
                combineUtcCalendarDateAndTime(endDate, toTime, { endOfSecond: true }),
              );
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface RouteAnalyticsFiltersProps {
  routeSearch: string;
  onRouteSearchChange: (value: string) => void;
  methods: string[];
  statusCodes: number[];
  createdFrom: Date | undefined;
  createdTo: Date | undefined;
  activePreset: RouteAnalyticsTimePreset;
  onApplyPreset: (presetId: Exclude<RouteAnalyticsTimePreset, "custom">) => void;
  onToggleMethod: (method: string) => void;
  onToggleStatusCode: (statusCode: number) => void;
  onCustomRange: (from: Date | undefined, to: Date | undefined) => void;
  onReset: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

export function RouteAnalyticsFilters({
  routeSearch,
  onRouteSearchChange,
  methods,
  statusCodes,
  createdFrom,
  createdTo,
  activePreset,
  onApplyPreset,
  onToggleMethod,
  onToggleStatusCode,
  onCustomRange,
  onReset,
  onRefresh,
  isRefreshing,
}: RouteAnalyticsFiltersProps) {
  const hasSecondaryFilters = Boolean(
    methods.length ||
      statusCodes.length ||
      routeSearch.trim() ||
      activePreset !== DEFAULT_ROUTE_ANALYTICS_PRESET,
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Input
            value={routeSearch}
            onChange={(e) => onRouteSearchChange(e.target.value)}
            placeholder="Search by route name…"
            className="h-8 w-full text-xs"
            aria-label="Search routes"
          />
        </div>

        <MultiSelectFilter
          label="Method"
          options={[...HTTP_METHODS]}
          selected={methods}
          onToggle={onToggleMethod}
        />
        <MultiSelectFilter
          label="Status code"
          options={STATUS_CODE_OPTIONS.map(String)}
          selected={statusCodes.map(String)}
          onToggle={(value) => onToggleStatusCode(Number.parseInt(value, 10))}
        />

        <div
          className="flex flex-wrap items-center gap-1 rounded-md border bg-muted/30 p-1"
          role="group"
          aria-label="Time range"
        >
          {ROUTE_ANALYTICS_TIME_PRESETS.map((preset) => (
            <Button
              key={preset.id}
              type="button"
              variant={activePreset === preset.id ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onApplyPreset(preset.id)}
            >
              {preset.label}
            </Button>
          ))}
          <CustomDateRangeButton from={createdFrom} to={createdTo} onChange={onCustomRange} />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={cn("size-3.5", isRefreshing && "animate-spin")} aria-hidden="true" />
          Refresh
        </Button>

        {hasSecondaryFilters ? (
          <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={onReset}>
            <X className="size-3.5" aria-hidden="true" />
            Reset filters
          </Button>
        ) : null}
      </div>

      {(createdFrom || createdTo) && activePreset === "custom" ? (
        <p className="text-xs text-muted-foreground">
          Custom range: {createdFrom ? formatUtcFilterInstant(createdFrom) : "…"} –{" "}
          {createdTo ? formatUtcFilterInstant(createdTo) : "…"}
        </p>
      ) : null}
    </div>
  );
}
